import * as React from 'react';
import clsx from 'clsx';
import { Box, InputAdornment, MenuItem, Paper, TextField, TextFieldProps } from '@material-ui/core';
import { fetchCompanies, fetchCompanyDetails } from '../helpers/apiCalls';
import { Autocomplete } from '@material-ui/lab';
import { IBaseCompany, ICompany, SearchProvider } from '../types/companyDetails';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useAppStateContext } from '../contexts/AppState';
import { RefObject } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    option: {
      textDecoration: 'underline',
      textDecorationThickness: 'from-font',
      '& .MuiButtonBase-root': {
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '1rem',
        letterSpacing: '0.025em',
        paddingBottom: theme.spacing(1.25),
        paddingTop: theme.spacing(1.25),
        '&:hover': {
          backgroundColor: 'var(--list-hover)'
        }
      }
    },
    companyHouseNumber: {
      marginRight: theme.spacing(-3)
    },
    autocomplete: {
      backgroundColor: 'var(--medium-gray)',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.015625em',
      borderRadius: '4px',
      '& .MuiAutocomplete-option[data-focus="true"], .MuiAutocomplete-option:active': {
        backgroundColor: 'var(--list-hover)'
      },
      '& .MuiAutocomplete-listbox': {
        paddingBottom: 0
      },
      '& .MuiAutocomplete-option': {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
        '&:hover': {
          backgroundColor: 'var(--list-hover)'
        }
      }
    }
  })
);

export const getItemId = (item: IBaseCompany) => {
  return item.abn || item.acn || item.id;
};

type OwnProps = {
  item?: ICompany;
  title: string;
  value: string;
  onChange: (companyName: string, details?: ICompany) => void;
  onManualSelected: () => void;
  isRequired: boolean;
  error: boolean;
  helperText: string | null;
  onClose?: () => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  provider?: SearchProvider;
  hasDetails?: boolean;
  disabled?: boolean;
};
type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'onFocus' | 'onBlur'> & OwnProps;
export const CompaniesContainer = React.forwardRef<RefObject<any>, Props>(
  (
    {
      item,
      value,
      title,
      onChange,
      onManualSelected,
      isRequired,
      disabled,
      error,
      helperText,
      onClose,
      onFocus,
      onBlur,
      provider,
      hasDetails
    },
    ref
  ) => {
    const classes = useStyles();
    const { t } = useTranslation('signup');
    const [open, setOpen] = React.useState<boolean>(false);
    const [companies, setCompanies] = React.useState<ICompany[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const { setScrollPosition } = useAppStateContext();
    const manualOption = t('fields.manualCompany', 'I don’t see my company listed');

    const search = React.useCallback(
      debounce(async (search: string) => {
        try {
          setIsLoading(true);
          if (!provider) return;
          const response = await fetchCompanies(provider, search);
          setCompanies(response.data);
        } catch (error) {
          throw error;
        } finally {
          setIsLoading(false);
        }
      }, 500),
      []
    );

    const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, newValue: string, reason: string) => {
      if (newValue !== value && newValue !== manualOption && reason !== 'reset') {
        onChange(newValue);
        if (newValue) {
          search(newValue);
          setOpen(true);
        } else {
          setOpen(false);
        }
      }
    };

    const handleSelect = async (_: React.ChangeEvent<HTMLInputElement>, value: ICompany | null, reason: string) => {
      if (reason === 'clear') {
        setCompanies([]);
      }
      if (value && value.companyName && value.companyName !== manualOption) {
        const company = { ...value };
        if (hasDetails && provider) {
          const { data } = await fetchCompanyDetails(provider, value);
          Object.assign(company, data);
        }
        onChange(company.companyName, company);
        setOpen(false);
      }
    };

    const handleManualOptionSelect = () => {
      setOpen(false);
      onManualSelected();
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      const contentContainer = document.getElementById('contentContainer');
      if (contentContainer) {
        setScrollPosition(e.target.getBoundingClientRect().top + contentContainer.scrollTop - 90);
      }
      onFocus && onFocus(e);
    };

    const handleClose = () => {
      setOpen(false);
      onClose && onClose();
    };

    function CustomPaper({ children, ...other }: React.HTMLAttributes<HTMLElement>) {
      return (
        <Paper {...other}>
          {children}
          <Box
            className={classes.option}
            onMouseDown={event => {
              event.preventDefault();
            }}
          >
            <MenuItem onClick={handleManualOptionSelect} value={manualOption}>
              {manualOption}
            </MenuItem>
          </Box>
        </Paper>
      );
    }

    return (
      <Autocomplete
        forcePopupIcon={false}
        open={open}
        disabled={disabled}
        onClose={handleClose}
        loading={isLoading}
        value={null}
        inputValue={value || ''}
        noOptionsText={t('fields.noResults', 'No results found')}
        loadingText={t('form.autoComplete.loading', 'Loading...')}
        options={companies}
        filterOptions={options => options}
        getOptionLabel={option => `${option.companyName} (${getItemId(option)})`}
        onChange={handleSelect}
        onInputChange={handleInputChange}
        PaperComponent={CustomPaper}
        classes={{ paper: classes.autocomplete }}
        fullWidth
        renderInput={params => (
          <TextField
            {...params}
            inputRef={ref}
            label={title}
            inputProps={{ ...params.inputProps, tabIndex: 1 }}
            variant="outlined"
            required={isRequired}
            error={error}
            helperText={helperText}
            onFocus={handleFocus}
            onBlur={onBlur}
            InputProps={{
              ...params.InputProps,
              endAdornment: item ? (
                <InputAdornment position="end" classes={{ root: clsx(!disabled && classes.companyHouseNumber) }}>
                  {getItemId(item)}
                </InputAdornment>
              ) : (
                params.InputProps.endAdornment
              )
            }}
          />
        )}
        freeSolo
      />
    );
  }
);
